/**
 * checkoutClient.ts
 *
 * Typed fetch wrapper for POST /api/bookings/checkout.
 * Called from client components (BookingForm) when the customer confirms
 * their date selection and submits contact info.
 *
 * The route is server-only — this wrapper is the only surface the browser
 * touches. It never carries a price; price computation happens server-side.
 */

import type { PostCheckoutFlow } from '@sifly/booking-flows/schema'
import { getBookingAttribution } from '@/lib/analytics'
import type { BookingTax } from '@sifly/shared-utils'

export type CheckoutResult = {
  clientSecret: string
  bookingId: string
  paymentIntentId: string
  expiresAt: string
  stripeAccountId: string
  publishableKey: string
  price: { totalCents: number; perDayCents: number; days: number }
  /** The flow definition PINNED to this booking (bookings.flow_version_id).
   *  Rendered as-is by the in-page runner so the pin stays the only source
   *  of truth — the client never re-resolves from booking_products.flow. */
  flow: PostCheckoutFlow
  /** What the server is about to charge — price + tax. Shown above the
   *  Payment Element so the customer sees the real number before paying,
   *  even when tax could not be pre-computed on an earlier step. */
  amountCents: number
  tax: BookingTax
}

export type StartCheckoutInput = {
  productId: string
  start: string
  end: string
  qty: number
  contact: {
    email: string
    firstName: string
    lastName: string
    phone?: string
  }
  /** If the customer is changing dates on an existing hold, supply the
   *  prior bookingId here so the server cancels it before re-holding. */
  releaseBookingId?: string
}

export async function startCheckout(
  input: StartCheckoutInput,
): Promise<
  | { ok: true; data: CheckoutResult }
  | { ok: false; status: number; error: string; reason?: string }
> {
  const res = await fetch('/api/bookings/checkout', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ ...input, analyticsAttribution: getBookingAttribution() }),
  })

  const json = await res.json().catch(() => ({})) as Record<string, unknown>

  if (!res.ok) {
    return {
      ok: false,
      status: res.status,
      error: (json.error as string) ?? 'Checkout failed',
      reason: json.reason as string | undefined,
    }
  }

  return { ok: true, data: json as CheckoutResult }
}
